import { apiCall } from "api-calls/api-call"
import {
  Balance,
  Batch,
  BatchDetail,
  Contract,
  ContractDestination,
  CreateContractBody,
  CutTitleRule,
  CutTitleRuleInput,
  DataItem,
  DataItemSearchMeta,
  ExecuteSelectionResult,
  Facets,
  GameOption,
  GameplayCutPreviewBody,
  GameplayCutPreviewResult,
  ListResponse,
  PlanSelectionResult,
  RecordingCutFlag,
  RecordingCutFlagInput,
  ReserveFilters,
  SearchDataItemsQuery,
  Selection,
  SelectionDetail,
  SelectionPolicy,
  SelectionReviewVerdict,
  SelectionSummary,
  UpdateContractBody,
  RebuildManifestResult,
  ValidateBatchResult,
} from "../store/types"

export const getGames = async(search?: string): Promise<{ games: GameOption[] }> => {
  return apiCall({ method: "GET", path: "data-delivery/games", query: search ? { search } : {} })
}

export const getFacets = async(): Promise<Facets> => {
  return apiCall({ method: "GET", path: "data-delivery/facets" })
}

export const getContracts = async(query: { clientId?: string; limit?: number; offset?: number }): Promise<ListResponse<Contract>> => {
  return apiCall({ method: "GET", path: "data-delivery/contracts", query })
}

export const getContract = async(id: string): Promise<{ contract: Contract | null }> => {
  return apiCall({ method: "GET", path: `data-delivery/contracts/${id}` })
}

export const createContract = async(body: CreateContractBody): Promise<{ contract: Contract }> => {
  return apiCall({ method: "POST", path: "data-delivery/contracts", body })
}

export const updateContract = async(id: string, body: UpdateContractBody): Promise<{ contract: Contract }> => {
  return apiCall({ method: "PATCH", path: `data-delivery/contracts/${id}`, body })
}

// Preflight the delivery bucket before reserving. POST because the probe writes
// and removes an object — it is not a safe read.
export const checkContractDestination = async(id: string, writeProbe?: boolean): Promise<ContractDestination> => {
  return apiCall({ method: "POST", path: `data-delivery/contracts/${id}/destination-check`, body: { writeProbe } })
}

export const getBalance = async(clientId: string, contractId: string): Promise<Balance> => {
  return apiCall({ method: "GET", path: `data-delivery/clients/${clientId}/contracts/${contractId}/balance` })
}

export const searchDataItems = async(
  query: SearchDataItemsQuery,
): Promise<ListResponse<DataItem> & { totalSeconds: number } & DataItemSearchMeta> => {
  return apiCall({ method: "GET", path: "data-delivery/data-items", query })
}

export const summarizeSelection = async(clientId: string, contractId: string, itemIds: string[]): Promise<SelectionSummary> => {
  return apiCall({ method: "POST", path: "data-delivery/selection/summary", body: { clientId, contractId, itemIds } })
}

export const executeBatch = async(body: {
  clientId: string
  contractId: string
  itemIds?: string[]
  requestedItems?: number
  allowPartial?: boolean
  deliverAll?: boolean
  filters?: ReserveFilters
  includeInputStreams?: boolean
  includeEventLogs?: boolean
  includeDeviceMetadata?: boolean
}): Promise<{ batch: Batch }> => {
  return apiCall({ method: "POST", path: "data-delivery/deliveries/execute", body })
}

export const getBatch = async(id: string, query: { itemsLimit?: number; itemsOffset?: number } = {}): Promise<BatchDetail> => {
  return apiCall({ method: "GET", path: `data-delivery/deliveries/${id}`, query })
}

export const getBatches = async(query: { contractId?: string; limit?: number; offset?: number }): Promise<ListResponse<Batch>> => {
  return apiCall({ method: "GET", path: "data-delivery/deliveries", query })
}

export const markDelivered = async(id: string): Promise<{ batch: Batch }> => {
  return apiCall({ method: "POST", path: `data-delivery/deliveries/${id}/mark-delivered` })
}

export const cancelBatch = async(id: string, reason?: string): Promise<{ batch: Batch }> => {
  return apiCall({ method: "POST", path: `data-delivery/deliveries/${id}/cancel`, body: { reason } })
}

export const reportCustomerRejection = async(id: string, reason: string, deliveryItemId?: string): Promise<unknown> => {
  return apiCall({
    method: "POST",
    path: `data-delivery/deliveries/${id}/customer-rejection`,
    body: { reason, deliveryItemId },
  })
}

// ── selections: plan → sample → review → execute ────────────────────────────
// Nothing is reserved until executeSelection, so a plan can be re-run and
// discarded freely.

export const planSelection = async(body: {
  clientId: string
  contractId: string
  name?: string
  policy?: SelectionPolicy
}): Promise<PlanSelectionResult> => {
  return apiCall({ method: "POST", path: "data-delivery/selections/plan", body })
}

export const getSelections = async(query: {
  contractId?: string
  limit?: number
  offset?: number
}): Promise<ListResponse<Selection>> => {
  return apiCall({ method: "GET", path: "data-delivery/selections", query })
}

export const getSelection = async(
  id: string,
  query: { sampledOnly?: boolean; limit?: number; offset?: number } = {},
): Promise<SelectionDetail> => {
  return apiCall({ method: "GET", path: `data-delivery/selections/${id}`, query })
}

export const sampleSelection = async(id: string, size?: number): Promise<unknown> => {
  return apiCall({ method: "POST", path: `data-delivery/selections/${id}/sample`, body: { size } })
}

export const reviewSelectionSession = async(
  id: string,
  sessionId: string,
  verdict: SelectionReviewVerdict,
  note?: string,
): Promise<unknown> => {
  return apiCall({ method: "POST", path: `data-delivery/selections/${id}/review`, body: { sessionId, verdict, note } })
}

export const executeSelection = async(
  id: string,
  body: {
    chunkSize?: number
    includeInputStreams?: boolean
    includeEventLogs?: boolean
    includeDeviceMetadata?: boolean
    skipReviewGate?: boolean
  },
): Promise<ExecuteSelectionResult> => {
  return apiCall({ method: "POST", path: `data-delivery/selections/${id}/execute`, body })
}

export const discardSelection = async(id: string, reason?: string): Promise<{ selection: Selection }> => {
  return apiCall({ method: "POST", path: `data-delivery/selections/${id}/discard`, body: { reason } })
}

// ── post-build batch actions ────────────────────────────────────────────────

export const validateBatch = async(id: string): Promise<ValidateBatchResult> => {
  return apiCall({ method: "POST", path: `data-delivery/deliveries/${id}/validate` })
}

// Republishes the JSON artifacts of a batch that already shipped. The reason is
// mandatory — it is stamped into the files themselves and onto the audit row.
export const rebuildBatchManifest = async(id: string, reason: string): Promise<RebuildManifestResult> => {
  return apiCall({ method: "POST", path: `data-delivery/deliveries/${id}/rebuild-manifest`, body: { reason } })
}

export const retryBatch = async(id: string): Promise<{ batch: Batch }> => {
  return apiCall({ method: "POST", path: `data-delivery/deliveries/${id}/retry` })
}

export const watermarkBatch = async(id: string): Promise<{ batchId: string; enqueued: boolean }> => {
  return apiCall({ method: "POST", path: `data-delivery/deliveries/${id}/watermark` })
}

export const getCutTitleRules = async(): Promise<{ rules: CutTitleRule[] }> => {
  return apiCall({ method: "GET", path: "data-delivery/cut-title-rules" })
}

export const putCutTitleRule = async(gameId: number, body: CutTitleRuleInput): Promise<{ rule: CutTitleRule }> => {
  return apiCall({ method: "PUT", path: `data-delivery/cut-title-rules/${gameId}`, body })
}

export const deleteCutTitleRule = async(gameId: number): Promise<{ deleted: boolean }> => {
  return apiCall({ method: "DELETE", path: `data-delivery/cut-title-rules/${gameId}` })
}

export const getRecordingCutFlags = async(query: { limit: number }): Promise<{ flags: RecordingCutFlag[] }> => {
  return apiCall({ method: "GET", path: "data-delivery/recording-cut-flags", query })
}

export const lookupRecordingCutFlags = async(recordingIds: string[]): Promise<{ flags: RecordingCutFlag[] }> => {
  return apiCall({ method: "POST", path: "data-delivery/recording-cut-flags/lookup", body: { recordingIds } })
}

export const putRecordingCutFlag = async(
  recordingId: string,
  body: RecordingCutFlagInput,
): Promise<{ flag: RecordingCutFlag }> => {
  return apiCall({ method: "PUT", path: `data-delivery/recording-cut-flags/${recordingId}`, body })
}

export const deleteRecordingCutFlag = async(recordingId: string): Promise<{ cleared: boolean }> => {
  return apiCall({ method: "DELETE", path: `data-delivery/recording-cut-flags/${recordingId}` })
}

export const previewGameplayCut = async(body: GameplayCutPreviewBody): Promise<GameplayCutPreviewResult> => {
  return apiCall({ method: "POST", path: "data-delivery/gameplay-cut/preview", body })
}
